import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {TextItemFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-font-selector';
import {useUpdateEffect} from 'ahooks';
import {TextSlideFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-font-selector';
import {TranscriptItemFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-item-font-selector/transcript-item-font-selector';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorTextItemFontPanelProps {
  panelId: string;
}

export default function PosterEditorTextItemFontPanel({...props}: PosterEditorTextItemFontPanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const onClose = useDefaultPanelClose(props.panelId);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && !activeSlide?.isTextSlide()) {
    return null;
  }

  if (!(activeItem.isSlideshow() || activeItem.isTranscript()) && !('textStyles' in activeItem)) {
    return null;
  }

  if (activeItem.isTranscript()) {
    return (
      <TranscriptItemFontSelector
        onClose={(): void => {
          onClose();
        }}
        showBackButton={false}
        showCloseButton
        isForMobile
      />
    );
  }

  if (activeItem.isSlideshow() && activeItem.getSelectedSlide().isTextSlide()) {
    return (
      <TextSlideFontSelector
        onClose={(): void => {
          onClose();
        }}
        showBackButton={false}
        showCloseButton
        isForMobile
      />
    );
  }

  return (
    <TextItemFontSelector
      onClose={(): void => {
        onClose();
      }}
      showBackButton={false}
      showCloseButton
      isForMobile
    />
  );
}
